import { useEffect, useState } from "@wordpress/element";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
// Removed Select import - using native select instead
import SyntaxHighlighter from "react-syntax-highlighter";
import { atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import {
	Check,
	X,
	Settings,
	Edit3,
	RotateCw,
	PlusCircle,
	Sparkles,
	Backpack,
	ArrowBigLeft,
	ChevronLeft,
	MoveLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import CodePreview from "@/components/code-preview";
import { useLicense } from "@/context/license-context";
import { useScan } from "@/context/scan-context";
import { SiteLogo } from "@/icons";
import { AnimatedItem } from "@/motions/animated-item";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AnimatedSparkle from "@/components/AnimatedSparkle";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import CustomSelect from "./custom-select";
const AI_PROVIDERS = {
	openai: {
		name: "OpenAI",
		endpoint: "https://api.openai.com/v1/chat/completions",
		models: [
			{ id: "gpt-4o", name: "GPT-4o" },
			{ id: "gpt-4o-mini", name: "GPT-4o Mini" },
			{ id: "gpt-4", name: "GPT-4" },
			{ id: "gpt-3.5-turbo", name: "GPT-3.5 Turbo" },
		],
	},
	anthropic: {
		name: "Anthropic",
		endpoint: "https://api.anthropic.com/v1/complete",
		models: [
			{ id: "claude-3-5-sonnet-20241022", name: "Claude 3.5 Sonnet" },
			{ id: "claude-3-sonnet-20240229", name: "Claude 3 Sonnet" },
			{ id: "claude-3-haiku-20240307", name: "Claude 3 Haiku" },
		],
	},
	openrouter: {
		name: "OpenRouter",
		endpoint: "https://openrouter.ai/api/v1/chat/completions",
		models: [
			{ id: "openai/gpt-4o", name: "GPT-4o (OpenRouter)" },
			{ id: "openai/gpt-4o-mini", name: "GPT-4o Mini (OpenRouter)" },
			{
				id: "anthropic/claude-3-5-sonnet",
				name: "Claude 3.5 Sonnet (OpenRouter)",
			},
			{ id: "mistralai/mixtral-8x7b-instruct", name: "Mixtral 8x7B" },
			{ id: "google/gemini-pro", name: "Gemini Pro" },
		],
	},
};

// Flatten models for easy selection
const getAllModels = () => {
	const models = [];
	Object.entries(AI_PROVIDERS).forEach(([providerId, provider]) => {
		provider.models.forEach((model) => {
			models.push({
				id: `${providerId}:${model.id}`,
				name: `${model.name} (${provider.name})`,
				provider: providerId,
				modelId: model.id,
			});
		});
	});
	return models;
};

const OffcanvasAssistant = () => {
	const { isValid, isActivated } = useLicense();
	const { issues, resolvedIssues, markResolved, runScan, progress } = useScan();
	const { ai } = useScan();
	const {
		apiKey,
		selectedModel,
		showApiKeyInput,
		tempApiKey,
		setTempApiKey,
		setShowApiKeyInput,
		saveApiKey,
		handleModelChange,
		askForIssue,
		loadingIssueId,
		aiSuggestions,
	} = ai;

	// Return null if license is not valid and activated
	if (!isValid || !isActivated) return null;

	// Control sheet visibility independent of issues cleanup
	const [showSheet, setShowSheet] = useState(false);
	const [aiPage, setAiPage] = useState("issue-page");

	// Initial param handling & auto scan logic
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const triggerScan = params.get("accessibility-scan") === "1";
		const showResults = params.get("accessibility-scanned") === "1";

		if (triggerScan) {
			setShowSheet(true);
			runScan();
			// Replace param so refresh shows results not re-scan loop
			window.history.replaceState({}, "", "?accessibility-scanned=1");
		} else if (showResults) {
			// If there are already results just show sheet; if not, run scan as fallback
			if (issues.length > 0) {
				setShowSheet(true);
			} else {
				setShowSheet(true);
				runScan();
			}
		}
	}, [runScan, issues.length]);

	// Removed auto-open on any existing issues to avoid showing on every page load without params.

	return (
		<Sheet open={showSheet} onOpenChange={(o) => setShowSheet(o)}>
			<SheetContent
				hideCloseButton={true}
				className="zn:w-full zn:max-w-2xl "
				id="accessibility-assistant-sheet"
			>
				<SheetHeader className="zn:sticky zn:top-0 zn:!bg-gray-200 zn:z-10  zn:!mb-8">
					<div className="zn:text-base  zn:flex zn:gap-2 zn:items-center zn:font-medium zn:text-gray-900">
						<div className="zn:flex-1 zn:flex zn:gap-2">
							<SiteLogo />
							<div className="zn:font-semibold">Accessibility Assistant</div>
						</div>
						<div className="zn:flex-none">
							<X
								onClick={() => {
									// Just hide the sheet and clean URL, keep results for quick reopen
									window.history.replaceState({}, "", window.location.pathname);
									setShowSheet(false);
								}}
							/>
						</div>
					</div>
				</SheetHeader>
				{aiPage === "issue-page" && (
					<div className="zn:!px-4 zn:h-[calc(100%-100px)] ">
						<AnimatedItem animation="scaleIn" delay={0.3}>
							<Card className="zn:!shadow-[0px_2px_1px_0px_rgba(0,0,0,0.08)] zn:!mb-6 zn:!border zn:!border-gray-200 zn:!p-4">
								<CardHeader>
									<CardTitle className="zn:flex zn:items-center zn:gap-3">
										{document.title}{" "}
										{resolvedIssues.length !== issues.length && (
											<Badge
												variant="destructive"
												className="zn:!bg-destructive/10 zn:rounded-full zn:!border-destructive zn:!border zn:!text-destructive "
											>
												{issues.length} Issue found
											</Badge>
										)}
									</CardTitle>
								</CardHeader>
								<CardContent>
									<Progress
										value={progress}
										className="zn:!bg-gray-200"
										activeClassName="zn:!bg-success"
									/>
									<div className="zn:!text-sm zn:text-gray-500 zn:leading-tight">
										Resolved {resolvedIssues.length}/{issues.length}
									</div>
									<div className="zn:flex zn:gap-3 zn:flex-wrap zn:!mt-3 zn:justify-end">
										<span
											onClick={() => runScan()}
											className="zn:!bg-primary-50 zn:!border zn:border-primary zn:inline-flex zn:!text-primary zn:text-xs zn:!px-3 zn:!py-1 zn:rounded zn:cursor-pointer  zn:items-center zn:gap-1"
										>
											New Scan
											<RotateCw className="zn:w-3 zn:h-3" />
										</span>
										{apiKey ? (
											<>
												<span
													className="zn:!bg-gray-100 zn:!border zn:border-gray-100 zn:inline-flex zn:!text-gray-700 zn:text-xs zn:!px-3 zn:!py-1 zn:rounded zn:cursor-pointer  zn:items-center zn:gap-1"
													onClick={() => {
														setShowApiKeyInput(true);
														setTempApiKey(apiKey);
														setAiPage("set-apikey");
													}}
												>
													<Edit3 className="zn:w-3 zn:h-3 zn:me-1" />
													Edit
												</span>
											</>
										) : (
											<span
												onClick={() => {
													setShowApiKeyInput(true);
													setAiPage("set-apikey");
												}}
												className="zn:!bg-gray-100 zn:!border zn:border-gray-100 zn:inline-flex zn:!text-gray-700 zn:text-xs zn:!px-3 zn:!py-1 zn:rounded zn:cursor-pointer  zn:items-center zn:gap-1"
											>
												Set API Key{" "}
												<PlusCircle className="zn:w-3 zn:h-3 zn:me-1" />
											</span>
										)}
									</div>
								</CardContent>
							</Card>
						</AnimatedItem>
						<ScrollArea className="zn:!h-[calc(100%-200px)]   ">
							{issues.length === 0 ? (
								<AnimatedItem animation="fadeInUp" delay={0.4}>
									No issues available to resolve.
								</AnimatedItem>
							) : (
								<div>
									<Accordion type="single" collapsible>
										{issues.map((issue, i) => (
											<AnimatedItem
												key={i}
												animation="fadeInUp"
												delay={i * 0.1}
											>
												<AccordionItem
													value={issue.id}
													className="zn:!border zn:!border-gray-200 zn:!mb-3 zn:!rounded-md zn:[&>h3]:!p-0"
												>
													<AccordionTrigger className="zn:!bg-transparent zn:!rounded-b-none zn:data-[state=open]:!bg-gray-100  zn:hover:no-underline zn:items-center   zn:!px-4 zn:!py-3 zn:!text-sm">
														<div>
															{resolvedIssues.includes(issue.id) && (
																<AnimatedItem
																	animation="scaleIn"
																	className="zn:inline-flex"
																>
																	<span
																		className={cn(
																			"zn:!size-4 zn:!text-white zn:items-center zn:justify-center zn:inline-flex zn:!border zn:!border-gray-300 zn:!relative zn:!top-1 zn:rounded-full zn:!me-1.5 zn:!bg-green-600",
																		)}
																	>
																		<Check className="zn:size-3" />
																	</span>
																</AnimatedItem>
															)}
															<strong>{issue.id}</strong>
														</div>
													</AccordionTrigger>
													<AccordionContent>
														<div className="zn:!px-4 zn:!overflow-auto zn:w-full">
															{issue.nodes.map((node, j) => (
																<div
																	className="zn:!my-2"
																	key={j}
																	onClick={() => {
																		const el = document.querySelector(
																			node.target[0],
																		);
																		if (el)
																			el.scrollIntoView({
																				behavior: "smooth",
																				block: "center",
																			});
																	}}
																>
																	<CodePreview
																		code={node.html}
																		title={node.target[0]}
																		language={node.target[1]}
																	/>
																</div>
															))}

															<div className="zn:flex zn:items-center zn:gap-2 zn:mt-3">
																{resolvedIssues.includes(issue.id) ? (
																	<span className="zn:text-xs zn:whitespace-nowrap zn:!bg-success zn:!text-white  zn:!px-2 zn:!py-1 zn:rounded zn:cursor-pointer">
																		Resolved
																	</span>
																) : (
																	<span
																		onClick={() => markResolved(issue.id)}
																		className="zn:text-xs zn:whitespace-nowrap zn:!bg-success/20 zn:!text-success zn:!border zn:!border-success zn:!px-2 zn:!py-1 zn:rounded zn:cursor-pointer"
																	>
																		Mark Resolved
																	</span>
																)}

																<span
																	className="zn:text-xs zn:whitespace-nowrap zn:!bg-[#CC5DE8] zn:!text-white zn:flex zn:items-center zn:gap-1.5  zn:!px-2 zn:!py-1 zn:rounded zn:cursor-pointer"
																	onClick={() => askForIssue(issue)}
																>
																	{loadingIssueId === issue.id ? (
																		"Asking AI..."
																	) : (
																		<>
																			<AnimatedSparkle className="zn:h-3.5 zn:w-3.5" />
																			<span>Let AI fix it for you</span>
																		</>
																	)}
																</span>

																{/* {apiKey && (
																<Button
																	size="sm"
																	variant="ghost"
																	onClick={() => {
																		setShowApiKeyInput(true);
																		setTempApiKey(apiKey);
																	}}
																	className="zn:h-6 zn:px-2 zn:text-xs zn:opacity-60 hover:zn:opacity-100"
																>
																	<Edit3 className="zn:w-3 zn:h-3" />
																</Button>
															)} */}
															</div>
														</div>

														{aiSuggestions[issue.id] && (
															<div className="zn:mt-4 zn:!px-4">
																<div className="zn:bg-blue-50 zn:border zn:border-blue-200 zn:rounded-lg zn:p-3">
																	<strong className="zn:text-blue-800 zn:text-sm zn:block zn:mb-2">
																		AI Suggestion (
																		{getAllModels().find(
																			(m) => m.id === selectedModel,
																		)?.name || "AI"}
																		):
																	</strong>
																	<CodePreview
																		code={aiSuggestions[issue.id]}
																		title="AI Suggestion"
																		language="jsx"
																	/>
																</div>
															</div>
														)}
													</AccordionContent>
												</AccordionItem>
											</AnimatedItem>
										))}
									</Accordion>
								</div>
							)}
						</ScrollArea>
					</div>
				)}
				{aiPage === "set-apikey" && (
					<div className="zn:!px-4 zn:h-full">
						<MoveLeft
							onClick={() => setAiPage("issue-page")}
							className="zn:mb-6 zn:cursor-pointer"
						/>
						<div className="zn:my-4 zn:p-3 zn:border zn:rounded-lg zn:bg-gray-50">
							<div className="zn:flex zn:items-center zn:gap-2 zn:mb-2">
								<Settings className=" zn:leading-none zn:relative zn:-top-1.5 zn:size-5" />
								<span className="zn:font-semibold zn:text-sm zn:!mb-3 zn:leading-none">
									AI Configuration
								</span>
							</div>

							{/* Model Selection */}
							<div className="zn:mb-3">
								<AnimatedItem
									animation="fadeInUp"
									delay={0.1}
									className="zn:!mt-2"
								>
									<CustomSelect
										label="AI Model"
										options={getAllModels()}
										value={selectedModel}
										onChange={handleModelChange}
									/>
								</AnimatedItem>
							</div>

							{/* API Key Management */}
							<AnimatedItem
								animation="fadeInUp"
								className="zn:flex zn:items-center zn:justify-end  zn:gap-2 zn:!mt-3"
							>
								{apiKey ? (
									<>
										<Button
											size="sm"
											variant="outline"
											onClick={() => {
												setShowApiKeyInput(true);
												setTempApiKey(apiKey);
											}}
											className="zn:h-6 zn:px-2 zn:text-xs"
										>
											<Edit3 className="zn:w-3 zn:h-3 zn:mr-1" />
											Edit
										</Button>
									</>
								) : (
									<Button
										size="sm"
										onClick={() => setShowApiKeyInput(true)}
										className="zn:h-6 zn:px-2 zn:text-xs"
									>
										Set API Key
									</Button>
								)}
							</AnimatedItem>

							{/* API Key Input */}
							{showApiKeyInput && (
								<AnimatedItem
									animation="fadeInUp"
									delay={0.2}
									className="zn:mt-3 zn:space-y-2"
								>
									<Label className="zn:text-xs zn:!mb-1.5">
										{selectedModel.startsWith("openai:")
											? "OpenAI API Key"
											: selectedModel.startsWith("anthropic:")
											? "Anthropic API Key"
											: "OpenRouter API Key"}
									</Label>
									<div className="zn:flex zn:gap-2">
										<Input
											type="password"
											placeholder={
												selectedModel.startsWith("openai:")
													? "sk-..."
													: selectedModel.startsWith("anthropic:")
													? "sk-ant-..."
													: "sk-or-v1-..."
											}
											value={tempApiKey}
											onChange={(e) => setTempApiKey(e.target.value)}
											className="zn:h-8 zn:text-xs"
										/>
										<Button
											size="sm"
											onClick={saveApiKey}
											className="zn:h-8 zn:px-3 zn:text-xs"
										>
											Save
										</Button>
										<Button
											size="sm"
											variant="outline"
											onClick={() => {
												setShowApiKeyInput(false);
												setTempApiKey("");
											}}
											className="zn:h-8 zn:px-3 zn:text-xs"
										>
											Cancel
										</Button>
									</div>
								</AnimatedItem>
							)}
						</div>
					</div>
				)}
			</SheetContent>
		</Sheet>
	);
};

export default OffcanvasAssistant;
